import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountStatus, Box, HelpIcon, Text, useResponsive } from '@common-fe/common-fe';
import _ from 'lodash';

import ExcessContributionWithdrawModal from '@/components/modals/ExcessContributionWithdrawModal/ExcessContributionWithdrawModal';
import { useExcessContributionAvailable } from '@/components/modals/ExcessContributionWithdrawModal/useExcessContributionAvailable';
import { useCIPInfo } from '@/modules/investments/cip/hooks';
import { useInvestmentsHoldings, useInvestor } from '@/modules/investments/hooks';
import { InvestorStatus } from '@/modules/investments/investments.types';
import {
  InvestmentsManagementItem,
  InvestmentsStartItem,
} from '@/modules/investments/InvestmentsFundsManagement';

import { useMyEnrollments } from '../hooks';

import { FundManagementOption } from './hooks/useFundManagementOptions';
import { FundsManagementItem } from './FundsManagementItem';

interface Props {
  fundsManagementItems: FundManagementOption[];
  accountId: string;
}

export const FundsManagementBanner: React.FC<Props> = ({ fundsManagementItems, accountId }) => {
  const { isExcessContributionAvailable } = useExcessContributionAvailable();
  const [isModalVisible, setModalVisible] = useState(false);
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const investor = useInvestor(accountId);
  const { currentMyAccount, isOpenHSAExist } = useMyEnrollments(accountId);
  const { isInvestorAccountExists } = useInvestmentsHoldings(accountId);
  const { isVerificationRequired } = useCIPInfo();
  const isNotVerified = useMemo(
    () => isVerificationRequired && currentMyAccount?.isCIPRequired,
    [currentMyAccount?.isCIPRequired, isVerificationRequired]
  );

  const isAccountClosed = useMemo(() => {
    return currentMyAccount?.accountStatus === AccountStatus.CLOSED;
  }, [currentMyAccount?.accountStatus]);
  const hideInvestmentItem = useMemo(
    () =>
      investor?.data?.status === InvestorStatus.ACTIVE ||
      investor?.data?.status === InvestorStatus.PENDING_EXTERNAL_CREATION ||
      !currentMyAccount?.allowInvestments,
    [currentMyAccount?.allowInvestments, investor?.data?.status]
  );
  if (
    _.isEmpty(fundsManagementItems) &&
    !isInvestorAccountExists &&
    (investor?.data?.status === InvestorStatus.ACTIVE ||
      investor?.data?.status === InvestorStatus.PENDING_EXTERNAL_CREATION ||
      !currentMyAccount?.allowInvestments)
  ) {
    return null;
  }
  return (
    <Box
      data-testid="funds-management-banner"
      margin={{ bottom: 'l' }}
      pad="spacing24"
      background={{ color: 'module' }}
      round="moduleRound"
    >
      <Box
        margin={{ bottom: 'spacing16' }}
        height={{ min: '40px' }}
        align="center"
        justify="between"
        direction="row"
      >
        <Text size="16px" weight={700}>
          {t('Funds Management')}
        </Text>
        {isExcessContributionAvailable && !isMobile ? (
          <Box data-testId="excessContributionHelp" onClick={() => setModalVisible(true)}>
            <Text color="textAccent">Excess Contribution Help</Text>
          </Box>
        ) : null}
        {isModalVisible ? (
          <ExcessContributionWithdrawModal onSetVisible={setModalVisible} isEditMode accountId={accountId} />
        ) : null}
      </Box>
      <Box gap="spacing16" direction={isMobile ? 'column' : 'row'}>
        {isInvestorAccountExists && !isNotVerified ? (
          <InvestmentsManagementItem disabled={isAccountClosed} accountId={accountId} />
        ) : (
          <InvestmentsStartItem
            disabled={isAccountClosed}
            isWideMode={_.isEmpty(fundsManagementItems)}
            accountId={accountId}
          />
        )}
        {fundsManagementItems?.map((item) => (
          <FundsManagementItem
            key={item.title}
            item={item}
            isOnlyOneItem={isMobile ? false : !isInvestorAccountExists && hideInvestmentItem}
          />
        ))}
      </Box>
      {isExcessContributionAvailable && isOpenHSAExist && isMobile ? (
        <Box
          data-testId="excessContributionHelp"
          onClick={() => setModalVisible(true)}
          margin={{ top: 'spacing8' }}
          direction="row"
          align="center"
        >
          <HelpIcon size="16px" color="textAccent" />
          <Text color="textAccent" margin={{ left: 'spacing4' }}>
            Excess Contribution Help
          </Text>
        </Box>
      ) : null}
    </Box>
  );
};
export default FundsManagementBanner;
